// Admin Payments Page — the full payment_events ledger, filtered and paginated.
// File: frontend/src/pages/AdminPaymentsPage.js
//
// Consumes GET /api/admin/payments (Task 1) and GET /api/admin/payments/export
// (Task 2) — see backend/app/routers/admin_payments.py. Both share one filter
// implementation (`apply_payment_filters`), so this page only needs to keep
// its query params consistent between the two requests, never re-derive them.
//
// `payment_ledger.SOURCES_WITH_WRITER` (backend/app/services/payment_ledger.py)
// declares which sources have a code path that writes ledger rows. Filtered
// to one that does not, this endpoint can only ever return zero rows — that
// zero means "not measured," not "no revenue from that source," so it gets
// its own message rather than falling into the generic "no payments match
// these filters" case. WHICH sources those are comes from the response's
// `sources_without_writer`, and so does the vendor NAMED in the message: it
// is Apple alone today, but the list is the backend's to change.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Button from '../components/Button';
import AdminPage from '../components/AdminShell';
import ConciergeBillingTable from '../components/billing/ConciergeBillingTable';
import { authConfig } from '../lib/conciergeBilling';
import './AdminPaymentsPage.css';

const PAGE_SIZE = 50;

const SOURCES = [
  { value: '', label: 'All sources' },
  { value: 'stripe_sub', label: 'Stripe' },
  { value: 'camp', label: 'Camps' },
  { value: 'apple', label: 'Apple' },
  { value: 'google', label: 'Google Play' },
  { value: 'concierge', label: 'Concierge' },
];

const SOURCE_LABEL = {
  stripe_sub: 'Stripe',
  camp: 'Camps',
  apple: 'Apple',
  google: 'Google Play',
  concierge: 'Concierge',
};

// One label lookup for every place a source is named — the table cell AND the
// writerless-source message below. The message used to say "Apple" as a
// literal while its trigger list came from the backend, so filtering to any
// OTHER writerless source told the admin a false thing about a vendor they
// had not selected.
const sourceLabel = (s) => SOURCE_LABEL[s] || s;

// The `est` badge's tooltip. It said "Estimated from list price" on EVERY
// derived row, which is true of at most two of the four sources. `est` is
// `price_source === 'derived'`, and derived means "net was modeled, not
// settled" — the reason differs:
//   - apple / google — the store never reports a per-transaction fee, so the
//     commission is assumed from the product price table
//     (payment_ledger.derived_amounts / commission_rate).
//   - stripe_sub / camp — the processor's own fee could not be READ, so the
//     row is written fee=0, net=gross (billing-service/app/ledger.py
//     amounts_from_charge; backend/app/routers/camps.py). Net is overstated.
// Telling a Stripe row's reader "estimated from list price" is not a vaguer
// version of that. It is a different claim, and a false one.
const STORE_SOURCES = ['apple', 'google'];
const estTitle = (source) => (
  STORE_SOURCES.includes(source)
    ? 'Modeled: list price less an assumed store commission. The store does not report a per-transaction fee.'
    : "Modeled: the processor's own fee could not be read, so it is recorded as $0.00 and net is overstated by whatever that fee was."
);

// The backend tells us which sources cannot record anything, in the list
// response's `sources_without_writer`. It is NOT hardcoded here: this fact
// already lives in payment_ledger.SOURCES_WITH_WRITER, and a third copy — a
// constant, the dashboard's has_writer flag, and this — is one that goes
// stale the day Phase 2 Task 7 ships the Apple writer, captioning real Apple
// revenue as unrecordable.
//
// Empty array when the field is absent (an older backend): the message it
// gates is an explanation, and withholding an explanation is safe, where
// showing a wrong one is not.

const STATUSES = [
  { value: '', label: 'All statuses' },
  { value: 'paid', label: 'Paid' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' },
];

const PRICE_SOURCES = [
  { value: '', label: 'Exact and estimated' },
  { value: 'exact', label: 'Exact only' },
  { value: 'derived', label: 'Estimated only' },
];

const EMPTY_FILTERS = { source: '', event_type: '', price_source: '', start: '', end: '' };

// net_cents is already the row's SIGNED contribution to net proceeds (paid
// positive, refunded negative, failed zero) — render it as-is, never re-run
// gross - fee here, or a refund silently reads as a second charge.
const dollars = (cents) => {
  const n = (cents || 0) / 100;
  const sign = n < 0 ? '-' : '';
  return `${sign}$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Server timestamps are naive UTC (no trailing Z) — append one so this parses
// as UTC instead of local time. Parsing follows AdminUsersPage's
// parseServerDate.
//
// RENDERED IN UTC, deliberately unlike AdminUsersPage, which renders in the
// viewer's zone. This page is a money page and three things have to agree on
// what day a row happened:
//   - the From/To filters, compared as NAIVE UTC server-side
//     (admin_payments.py `_parse_range_bound` / `apply_payment_filters`)
//   - the CSV export, whose column is named `occurred_at_utc`
//   - this table
// Formatting in local time breaks all three apart by up to a day: west of
// Greenwich a 2026-08-26T02:00:00 row renders "Aug 25", so an admin closing
// August sees a row the filter that produced it says is out of range, and the
// file they downloaded disagrees with both. The zone is stated in the column
// header and the ledger note so nobody has to infer it.
const fmtDate = (iso) => {
  if (!iso) return null;
  const hasTz = /([zZ])|([+-]\d{2}:?\d{2})$/.test(iso);
  const d = new Date(hasTz ? iso : `${iso}Z`);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', {
    timeZone: 'UTC', year: 'numeric', month: 'short', day: 'numeric',
  });
};

const errorDetail = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

const TABS = [
  { value: 'ledger', label: 'Ledger' },
  { value: 'concierge', label: 'Concierge' },
];

const yesNo = (v) => (v ? 'Yes' : 'No');

// The Concierge tab: every concierge payment request, ProZpects' and each
// partner's, filterable by seller, plus each partner's Stripe Connect status
// (spec 2026-11-03-concierge-stripe-payments §6). Partner payments go to the
// partner's own Stripe account and never reach the ledger above, so this is
// the only place staff see them.
function ConciergeTab() {
  const { API_URL } = useAuth();
  const [seller, setSeller] = useState('');
  const [rows, setRows] = useState(null);
  const [rowsErr, setRowsErr] = useState(false);
  const [accounts, setAccounts] = useState(null);
  const [accountsErr, setAccountsErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/concierge-billing/accounts`, authConfig())
      .then((r) => { if (!cancelled) setAccounts(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setAccountsErr(true); });
    return () => { cancelled = true; };
  }, [API_URL]);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setRowsErr(false);
    const qs = seller ? `?seller=${encodeURIComponent(seller)}` : '';
    axios.get(`${API_URL}/concierge-billing/requests${qs}`, authConfig())
      .then((r) => { if (!cancelled) setRows(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setRowsErr(true); });
    return () => { cancelled = true; };
  }, [API_URL, seller]);

  return (
    <>
      <div className="card pay-filters">
        <div className="form-field">
          <label className="form-field__label" htmlFor="pay-concierge-seller">Seller</label>
          <select
            id="pay-concierge-seller"
            className="filter-select"
            value={seller}
            onChange={(e) => setSeller(e.target.value)}
          >
            <option value="">All sellers</option>
            <option value="prozpects">ProZpects</option>
            {(accounts || []).map((a) => (
              <option key={a.partner_user_id} value={String(a.partner_user_id)}>{a.partner_name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="adm-stack-cards">
        <div className="adm__card">
          {rowsErr ? (
            <div role="alert">Couldn&rsquo;t load concierge payment requests. Try again.</div>
          ) : !rows ? (
            <div className="adm-loading">Loading…</div>
          ) : (
            <ConciergeBillingTable rows={rows} showSeller />
          )}
        </div>

        <div className="adm__card">
          <h2 className="adm-card__title">Partner Stripe accounts</h2>
          {accountsErr ? (
            <div role="alert">Couldn&rsquo;t load partner Stripe accounts.</div>
          ) : !accounts ? (
            <div className="adm-loading">Loading…</div>
          ) : accounts.length === 0 ? (
            <p className="adm-empty">No partner has connected Stripe yet.</p>
          ) : (
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Partner</th>
                  <th>Connected</th>
                  <th>Charges enabled</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((a) => (
                  <tr key={a.partner_user_id}>
                    <td>{a.partner_name}</td>
                    <td>{yesNo(a.connected)}</td>
                    <td>{yesNo(a.charges_enabled)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

export default function AdminPaymentsPage() {
  const { API_URL } = useAuth();
  const [tab, setTab] = useState('ledger');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState(null);
  const [exportCapped, setExportCapped] = useState(false);
  const [exportFailed, setExportFailed] = useState(false);

  const queryString = useCallback((extra = {}) => {
    const params = new URLSearchParams();
    Object.entries({ ...filters, ...extra }).forEach(([k, v]) => {
      if (v) params.set(k, String(v));
    });
    return params.toString();
  }, [filters]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const qs = queryString({ offset, limit: PAGE_SIZE });
    axios.get(`${API_URL}/admin/payments?${qs}`)
      .then(({ data: d }) => {
        if (cancelled) return;
        setData(d);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        // A failed request must never fall through to the empty-table UI —
        // that would present "we don't know" as "there is nothing here."
        setData(null);
        setError(errorDetail(err, 'The request failed. Try again.'));
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [queryString, offset, API_URL]);

  const setFilter = (key) => (e) => {
    setOffset(0);
    setFilters((f) => ({ ...f, [key]: e.target.value }));
  };

  const clearFilters = () => {
    setOffset(0);
    setFilters(EMPTY_FILTERS);
  };

  const filtersActive = Object.values(filters).some(Boolean);
  const items = data?.items || [];

  // The SELECTED source, when the backend has told us it has no ledger writer.
  // Null otherwise — including when `sources_without_writer` is absent (an
  // older backend), because withholding an explanation is safe where showing
  // the wrong one is not.
  const writerlessSource = (data?.sources_without_writer || []).includes(filters.source)
    ? filters.source
    : null;

  // EVERY writerless source, whether or not one is selected — the ledger note
  // names them unconditionally. The message above only fires once an admin has
  // filtered to one, and the state this page launches in has no filter at all:
  // `sources_without_writer: ['apple']` arrived on the very first response and
  // nothing rendered it.
  const writerlessNames = (data?.sources_without_writer || []).map(sourceLabel);
  const writerlessPhrase = writerlessNames.length > 1
    ? `${writerlessNames.slice(0, -1).join(', ')} and ${writerlessNames[writerlessNames.length - 1]}`
    : writerlessNames[0];

  const exportCsv = async () => {
    setExporting(true);
    setExportMessage(null);
    setExportCapped(false);
    setExportFailed(false);
    try {
      const qs = queryString();
      const res = await axios.get(
        `${API_URL}/admin/payments/export${qs ? `?${qs}` : ''}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `payments-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const rowCount = res.headers?.['x-export-row-count'];
      setExportMessage(
        rowCount ? `Downloaded ${Number(rowCount).toLocaleString('en-US')} rows.` : 'Download started.'
      );
    } catch (err) {
      // responseType: 'blob' means an error body (400/413) also arrives as a
      // Blob instead of parsed JSON — read it back ourselves so the real
      // server message (e.g. the 413's row count and cap) reaches the admin.
      // Pattern from AdminWristbandsPage.js.
      // The 413 branch gets its OWN fallback. It is rendered behind the
      // "Export too large — " prefix, and the generic text produced
      // "Export too large — Could not export. Try again.": a sentence that
      // diagnoses and then disclaims the diagnosis. A capped export is a
      // known, actionable condition even when its body did not parse.
      const status = err?.response?.status;
      let detail = status === 413
        ? 'Narrow the date range or add a filter and try again.'
        : 'Could not export. Try again.';
      try {
        const text = await err.response.data.text();
        const parsed = JSON.parse(text);
        if (typeof parsed.detail === 'string') detail = parsed.detail;
      } catch (_parseErr) {
        // Not a JSON blob (e.g. a network error with no response body) —
        // fall through to the generic message set above.
      }
      setExportMessage(detail);
      setExportCapped(status === 413);
      setExportFailed(status !== 413);
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminPage
      active="payments"
      title="Payments"
      subtitle={tab === 'concierge'
        ? 'Concierge payment requests, ProZpects and partners'
        : data ? `${data.total.toLocaleString('en-US')} payments matched` : loading ? 'Loading…' : 'Could not load the total'}
    >

      <div className="adm-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={tab === t.value}
            className={`adm-tab${tab === t.value ? ' adm-tab--active' : ''}`}
            onClick={() => setTab(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'concierge' ? <ConciergeTab /> : (
        <>
        {/*
          UNCONDITIONAL. This whole note used to be gated on
          `data?.ledger_starts_at`, which is NULL exactly when the ledger is
          empty — the state the page launches in. So the one render where an
          admin has nothing to check the caveat against, and reads "The ledger
          is empty" as "ProZpects has taken no payments", was the one render
          with no caveat: no UTC, no exact-versus-modeled, no note that Apple
          cannot be recorded at all. Only the ledger-START clause is
          conditional now, because only that clause needs a date to exist.

          On the accuracy wording: `est` is `price_source === 'derived'`, and
          `derived` does NOT mean "a store commission was assumed". It means
          the row's NET was modeled rather than settled, and the cause differs
          by source. This caption used to vouch for Stripe and camp as exact
          outright, while both write `derived` rows on exactly the path that
          matters most:
            - billing-service/app/ledger.py `amounts_from_charge` — when the
              charge's balance_transaction fee cannot be read:
              InvoiceAmounts(gross, 0, gross, "derived", charge_id).
            - backend/app/routers/camps.py and
              backend/scripts/backfill_camp_payments.py — identically:
              `fee, net, price_source = 0, gross, "derived"`.
          Both files' own comments say it precisely: "an 'exact' row carrying
          an un-netted figure is a lie the dashboard can't detect." So a
          `stripe_sub` row can carry `est` with fee $0.00 and net == gross —
          net OVERSTATED — which the old caption steered the reader away from
          noticing by telling them Stripe was exact.
        */}
        <p className="pay-ledger-note">
          {data?.ledger_starts_at ? `Ledger begins ${fmtDate(data.ledger_starts_at)}. ` : ''}
          Every date on this page, in the From/To filters and in the exported file
          is <strong>UTC</strong>, not your local time. This is not a complete financial
          record. A row marked <span className="pay-est">est</span> carries a modeled
          figure, not one the processor reported, and what was modeled depends on the
          source: for Apple and Google Play the store commission is assumed from list
          price; for Stripe and camps it means the processor&rsquo;s own fee could not be
          read, so the fee is recorded as $0.00 and net is overstated by whatever that
          fee was.
          {/*
            Deliberately NOT the empty-state card's wording ("… has no ledger
            writer yet …"). Both surfaces can be on screen at once, and while
            they were phrased alike this <p> shadowed the card in every
            findByText for that phrase — the writerless test above silently
            started asserting against the caption instead of the message it
            was written for, and passed on the caption's copy of the words.
            Two surfaces, two sentences.
          */}
          {writerlessPhrase
            ? ` No ${writerlessPhrase} payment can be recorded at all yet — nothing in `
              + `this codebase writes ${writerlessPhrase} rows — so a zero for `
              + `${writerlessNames.length > 1 ? 'those sources' : 'it'} here is `
              + `unmeasured, not zero revenue.`
            : ''}
        </p>

        <div className="card pay-filters">
          <div className="form-field">
            <label className="form-field__label" htmlFor="pay-filter-source">Source</label>
            <select
              id="pay-filter-source"
              className="filter-select"
              value={filters.source}
              onChange={setFilter('source')}
            >
              {SOURCES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label className="form-field__label" htmlFor="pay-filter-status">Status</label>
            <select
              id="pay-filter-status"
              className="filter-select"
              value={filters.event_type}
              onChange={setFilter('event_type')}
            >
              {STATUSES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label className="form-field__label" htmlFor="pay-filter-accuracy">Accuracy</label>
            <select
              id="pay-filter-accuracy"
              className="filter-select"
              value={filters.price_source}
              onChange={setFilter('price_source')}
            >
              {PRICE_SOURCES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label className="form-field__label" htmlFor="pay-filter-start">From</label>
            <input
              id="pay-filter-start"
              className="form-field__input"
              type="date"
              value={filters.start}
              onChange={setFilter('start')}
            />
          </div>
          <div className="form-field">
            <label className="form-field__label" htmlFor="pay-filter-end">To</label>
            <input
              id="pay-filter-end"
              className="form-field__input"
              type="date"
              value={filters.end}
              onChange={setFilter('end')}
            />
          </div>
          {filtersActive && (
            <Button type="button" variant="secondary" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="pay-export-btn"
            onClick={exportCsv}
            loading={exporting}
            loadingText="Exporting…"
          >
            Export CSV
          </Button>
        </div>

        {exportMessage && (
          <div
            className={`settings-toast ${exportCapped || exportFailed ? 'settings-toast--error' : ''}`}
            role="alert"
          >
            {exportCapped ? `Export too large — ${exportMessage}` : exportMessage}
          </div>
        )}

        {loading && <div className="empty-state-card">Loading payments…</div>}

        {!loading && error && (
          <div className="empty-state-card" role="alert">
            <div className="empty-state-card__icon">⚠️</div>
            Couldn&rsquo;t load payments: {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="empty-state-card">
            {!filtersActive && (data?.total ?? 0) === 0 ? (
              // "No payment events have been recorded yet" — a statement about
              // the LEDGER. Not "no payments have been taken", which is what an
              // admin reads off an empty page and is not something this page
              // knows. Nothing has been backfilled and Apple cannot be recorded
              // at all; the note above carries both, and this sentence points
              // at it rather than quietly contradicting it.
              'The ledger is empty — no payment events have been recorded in it yet. '
                + 'That is not the same as no payments having been taken; see the note above.'
            ) : writerlessSource ? (
              `${sourceLabel(writerlessSource)} has no ledger writer yet, so this filter can't show `
                + `data — it does not mean ${sourceLabel(writerlessSource)} revenue is zero.`
            ) : (
              'No payments match these filters.'
            )}
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <div className="card pay-table-card">
            <table className="pay-table">
              <thead>
                <tr>
                  <th>When (UTC)</th>
                  <th>Name</th>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Gross</th>
                  <th>Fee</th>
                  <th>Net</th>
                  <th>Transaction</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id}>
                    <td>{fmtDate(row.occurred_at)}</td>
                    <td className="pay-name">{row.name}</td>
                    <td>
                      {SOURCE_LABEL[row.source] || row.source}
                      {row.price_source === 'derived' && (
                        <span className="pay-est" title={estTitle(row.source)}>est</span>
                      )}
                    </td>
                    <td>
                      <span className={`pay-status pay-status--${row.event_type}`}>{row.event_type}</span>
                    </td>
                    <td>{dollars(row.gross_cents)}</td>
                    <td>{dollars(row.fee_cents)}</td>
                    <td className="pay-net">{dollars(row.net_cents)}</td>
                    <td className="pay-txn">{row.provider_txn_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="pay-pager">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(offset - PAGE_SIZE, 0))}
          >
            Previous
          </Button>
          <span>
            {items.length > 0
              ? `${(offset + 1).toLocaleString('en-US')}–${(offset + items.length).toLocaleString('en-US')} of ${(data?.total ?? 0).toLocaleString('en-US')}`
              : '—'}
          </span>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={!data?.has_more}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </Button>
        </div>
        </>
      )}
    </AdminPage>
  );
}
